<!--
  A resume point, as a card: wide art, what it is, and how much is left. Tapping
  it goes straight back into the film at the right second — no detail page in the
  way, because that is the whole point of a resume point.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import Art from './Art.svelte';
  import Button from './Button.svelte';
  import Sheet from './Sheet.svelte';
  import Icon from './Icon.svelte';
  import { backgroundUrl, posterUrl } from './cinemeta';
  import { episodeCode, remaining } from './format';
  import { watchHref } from './nav.svelte';
  import { forgetProgress, type Progress } from './prefs.svelte';

  interface Props {
    item: Progress;
    eager?: boolean;
    /**
     * Every progress key this card stands for. A series tile is ONE tile for
     * the whole show (see `continueTiles` in history-groups.ts), so its "x"
     * has to forget the show — dropping only the episode drawn on it would
     * redraw the same series a frame later with the previous episode on it,
     * which reads as "the remove button does not work". Defaults to just this
     * item's key, which is what a film wants.
     */
    forgetKeys?: string[];
  }

  let { item, eager = false, forgetKeys }: Props = $props();
  const keys = $derived(forgetKeys && forgetKeys.length > 0 ? forgetKeys : [item.key]);
  /* Copy follows the same rule: one episode says "this episode", a show says
     what actually happens, which is that the show leaves the row. */
  const wholeShow = $derived(item.kind === 'series' && keys.length > 1);
  let confirming = $state(false);
  let card = $state<HTMLAnchorElement | null>(null);
  let removeButton = $state<HTMLButtonElement | null>(null);

  async function cancelRemoval(): Promise<void> {
    confirming = false;
    await tick();
    if (removeButton?.isConnected) removeButton.focus();
  }

  async function remove(): Promise<void> {
    // Close first so the dialog restores focus before its card leaves the DOM.
    // Rail copies can repeat this key, so find a different resume point.
    const rail = card?.closest('section');
    const next = Array.from(rail?.querySelectorAll<HTMLAnchorElement>('a[data-progress-key]') ?? [])
      .find((link) => link.dataset.progressKey !== item.key && !link.closest('[inert]'));
    confirming = false;
    await tick();
    for (const key of keys) forgetProgress(key);
    await tick();
    const fallback = document.querySelector<HTMLElement>('main a[href], main button');
    (next?.isConnected ? next : fallback)?.focus();
  }

  const href = $derived(
    watchHref({
      kind: item.kind,
      imdbId: item.imdbId,
      season: item.season ?? null,
      episode: item.episode ?? null
    })
  );
  const percent = $derived(
    item.durationSec > 0 ? Math.min(100, (item.positionSec / item.durationSec) * 100) : 0
  );
  const line = $derived(
    item.kind === 'series' && item.season !== undefined && item.episode !== undefined
      ? `${episodeCode(item.season, item.episode)}${item.subtitle ? ` · ${item.subtitle}` : ''}`
      : ''
  );
</script>

<div class="wrap">
  <a bind:this={card} class="card" data-progress-key={item.key} {href} title="Resume {item.title}" aria-label={`Resume ${item.title}${line ? `, ${line}` : ''}, ${remaining(item.positionSec, item.durationSec) || 'Just started'}`} >
    <div class="art">
      <Art
        src={item.background ?? backgroundUrl(item.imdbId)}
        fallbacks={[item.poster ?? posterUrl(item.imdbId)]}
        alt=""
        label={item.title}
        ratio="16 / 9"
        rounded={false}
        {eager}
      />
      <span class="scrim" aria-hidden="true"></span>
      <span class="play" aria-hidden="true"><Icon name="play" size={16} /><span>Resume</span></span>
      <div class="bar" aria-hidden="true"><i style:width="{percent}%"></i></div>
    </div>
    <div class="text">
      <p class="name">{item.title}</p>
      <p class="sub">
        {#if line}<span class="ep">{line}</span>{/if}
        <span class="remaining">{remaining(item.positionSec, item.durationSec) || 'Just started'}</span>
      </p>
    </div>
  </a>

  <!-- Drawn on the card, inside the link's own rectangle, so a remote would have
       two answers for one tile. Not a d-pad destination, for the reason spelled
       out at the same control in `Poster.svelte`. -->
  <button
    type="button"
    class="drop"
    bind:this={removeButton}
    data-dpad-skip
    title="Remove from Continue"
    aria-label="Remove {item.title} from Continue"
    aria-haspopup="dialog"
    onclick={() => (confirming = true)}
  >
    <Icon name="close" size={16} />
  </button>
</div>

{#if confirming}
  <Sheet open title="Remove from Continue?" onclose={() => void cancelRemoval()}>
    <p class="remove-detail">
      {#if wholeShow}
        This clears your saved playback positions for <strong>{item.title}</strong> — all {keys.length} episodes on this device.
      {:else}
        This clears your saved playback position for <strong>{item.title}</strong>{#if line} ({line}){/if}.
      {/if}
    </p>
    {#snippet footer()}
      <div class="confirm-actions">
        <Button onclick={() => void cancelRemoval()}>Keep watching</Button>
        <button type="button" class="confirm-remove" onclick={remove}>Remove</button>
      </div>
    {/snippet}
  </Sheet>
{/if}

<style>
  .wrap {
    position: relative;
  }

  .card {
    display: block;
    border-radius: var(--radius);
    touch-action: manipulation;
  }

  .art {
    position: relative;
    border-radius: var(--radius);
    overflow: hidden;
    background: var(--surface-2);
    transition:
      transform var(--dur) var(--ease),
      box-shadow var(--dur) var(--ease);
  }
  .card:hover .art {
    transform: translateY(-2px);
    box-shadow: var(--shadow-2);
  }
  .card:focus-visible {
    outline: none;
  }
  .card:focus-visible .art {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  .scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, color-mix(in oklab, var(--bg) 80%, transparent), transparent 70%);
  }

  .play {
    position: absolute;
    left: var(--space-3);
    bottom: var(--space-4);
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: 34px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-pill);
    background: var(--text);
    color: var(--bg);
    font-size: var(--text-xs);
    font-weight: var(--weight-semi);
  }

  .bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: var(--surface-3);
  }
  .bar i {
    display: block;
    height: 100%;
    background: var(--accent);
  }

  .text {
    margin-top: var(--space-3);
  }
  .name {
    font-size: var(--text-base);
    font-weight: 600;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    margin-top: var(--space-1);
    color: var(--text-dim);
    font-size: var(--text-sm);
    line-height: 1.45;
  }
  .sub .ep {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .remaining {
    font-variant-numeric: tabular-nums;
  }

  .drop {
    position: absolute;
    top: var(--space-2);
    right: var(--space-2);
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    border-radius: var(--radius-pill);
    background: var(--nav-fill);
    border: 1px solid var(--nav-line);
    color: var(--text);
    touch-action: manipulation;
    opacity: 0;
    transition:
      opacity var(--dur-fast) var(--ease),
      background-color var(--dur-fast) var(--ease);
  }
  .wrap:hover .drop,
  .wrap:focus-within .drop,
  .drop:focus-visible {
    opacity: 1;
  }
  .drop:hover {
    background: var(--nav-fill-hover);
  }
  .drop:focus-visible,
  .confirm-remove:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }
  .remove-detail {
    padding: var(--space-3) var(--space-4);
    color: var(--text-dim);
    font-size: var(--text-base);
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
  .remove-detail strong {
    color: var(--text);
  }
  .confirm-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: var(--space-3);
  }
  .confirm-actions :global(.btn),
  .confirm-remove {
    min-height: var(--hit);
  }
  .confirm-remove {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    background: var(--surface-2);
    border: 1px solid var(--bad);
    color: var(--bad);
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    touch-action: manipulation;
  }
  .confirm-remove:hover {
    background: var(--bg);
  }
  @media (prefers-reduced-motion: reduce) {
    .art, .drop {
      transition: none;
    }
    .card:hover .art {
      transform: none;
    }
  }

  /* No hover on a phone, so the control is always there but quiet. */
  @media (hover: none) {
    .drop {
      opacity: 1;
    }
  }
</style>
